import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { isImpersonating } from "../../Impersonation/impersonationState";
import { ContextVoter } from "./composables/ContextVoter";

export class AppSubscriptionVoter extends ContextVoter implements ContextVoterInterface {
  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAppsubscriptionCreateany,
      contextAttribute: PermissionAttributesEnum.ContextClientOperationAppsubscriptionCreate,
    });
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAppsubscriptionReadany,
      contextAttribute: PermissionAttributesEnum.ContextClientOperationAppsubscriptionRead,
    });
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAppsubscriptionDeleteany,
      contextAttribute: PermissionAttributesEnum.ContextClientOperationAppsubscriptionDelete,
    });
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAppsubscriptionUpdateany,
      contextAttribute: PermissionAttributesEnum.ContextClientOperationAppsubscriptionUpdate,
    });
  };

  // Share management, as the API's AppSubscriptionShareVoter decides it: the install owner always manages their
  // own shares; otherwise update rights on the install's client, or the dedicated app_subscription_share keys.
  public canManageShares = (
    subscription: { owner?: string | null, client?: string | null },
    currentUserIri: string | null | undefined,
  ): boolean => {
    // Sharing hands out access: never as someone else (the API refuses it while impersonating).
    if (isImpersonating.value) {
      return false;
    }

    if (currentUserIri && subscription.owner === currentUserIri) {
      return true;
    }

    if (this.canUpdate({ client: subscription.client })) {
      return true;
    }

    return this._canCrud({
      item: { client: subscription.client },
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationAppsubscriptionshareCreateany,
        PermissionAttributesEnum.GlobalOperationAppsubscriptionshareUpdateany,
        PermissionAttributesEnum.GlobalOperationAppsubscriptionshareDeleteany,
      ],
      contextAttribute: [
        PermissionAttributesEnum.ContextClientOperationAppsubscriptionshareCreate,
        PermissionAttributesEnum.ContextClientOperationAppsubscriptionshareUpdate,
        PermissionAttributesEnum.ContextClientOperationAppsubscriptionshareDelete,
      ],
    });
  };

  public canManage: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationAppsubscriptionCreateany,
        PermissionAttributesEnum.GlobalOperationAppsubscriptionUpdateany,
        PermissionAttributesEnum.GlobalOperationAppsubscriptionDeleteany,
      ],
      contextAttribute: [
        PermissionAttributesEnum.ContextClientOperationAppsubscriptionCreate,
        PermissionAttributesEnum.ContextClientOperationAppsubscriptionUpdate,
        PermissionAttributesEnum.ContextClientOperationAppsubscriptionDelete,
      ],
    });
  };
}
